import type { AxiosResponse } from 'axios';

import type { requestStatus } from '../constants/appConstants';
import type { RequestType } from '../zustandStore/slices';

import HttpRepository from './httpRepository';
type Approver = Record<string, unknown>;

type Costcentercode = {
	listId: string;
	listLabel: string;
};

export type OutOfOffice = {
	OOO_ENABLED: boolean;
	OOO_START_DATE: Date;
	OOO_END_DATE: Date;
	OOO_FORWARD_TO_FULL_NAME: string;
	OOO_FORWARD_TO: string;
	OOO_FORWARD_TO_EMAIL: string;
};

export type TeamProperties = {
	DEFAULT_APPROVER: string;
	INVITED_DATE: Date;
	REQUEST_LIMIT: string;
} & OutOfOffice;

type Coding = {
	costcentercode: Costcentercode;
};

type Fields = {
	amount: string;
	comments: string;
	description: string;
	endDate: Date;
};

export type Requester = {
	email: string;
	firstName: string;
	lastName: string;
	properties: Approver;
	teamApproverEmail: string;
	teamProperties: TeamProperties;
	userId: string;
};

type RequestStatus = (typeof requestStatus)[keyof typeof requestStatus];

type GetRequestRespone = {
	amount: string;
	approver: Approver;
	attachments: [];
	authPaddingPercentage: number;
	businessId: string;
	cardType: string;
	coding: Coding;
	companyId: string;
	currency: string;
	fields: Fields;
	isArchived: boolean;
	lastEdit: string;
	profileId: string;
	requestId: string;
	requester: Requester;
	state: RequestStatus;
	teamId: string;
	title: string;
	virtualCardId?: string;
};

type AddTagOptions = {
	requestId: string;
	tag: string;
};

type ApproveOptions = {
	requestId: string;
	[key: string]: any;
};

type CancelRequestOptions = {
	requestId: string;
};

type GetRequestOptions = {
	requestId: string;
};

type GetRequestsOptions = {
	archived?: boolean;
};

type RejectOptions = {
	requestId: string;
	taskId: string;
};

type RemoveTagOptions = {
	requestId: string;
	tag: string;
};

type SubmitRequestOptions = {
	requestId: string;
	data: {
		cardType: string;
		coding: any;
		currency: string;
		fields: any;
		profileId: string;
		requestId: string;
		teamId: string;
	};
};

type RegisterRequestOptions = {
	requestId: string;
};

const RequestRepository = {
	getRequestList: () =>
		HttpRepository.tradeshiftGo()
			.get('/rest/requests?archived=false')
			.then((response: AxiosResponse<RequestType[]>) => {
				return response.data;
			}),

	addTag: (options: AddTagOptions): Promise<any> =>
		HttpRepository.tradeshiftGo()
			.put(`/rest/requests/${options.requestId}/tags/${options.tag}`)
			.then(response => {
				return response.data;
			}),

	approve: (options: ApproveOptions): Promise<any> =>
		HttpRepository.tradeshiftGo()
			.post(`/rest/requests/${options.requestId}/approve`, options)
			.then(response => {
				return response.data;
			}),

	cancelRequest: (options: CancelRequestOptions): Promise<any> =>
		HttpRepository.tradeshiftGo()
			.delete(`/rest/requests/${options.requestId}`)
			.then(response => {
				return response.data;
			}),

	getRequest: (options: GetRequestOptions) => {
		return HttpRepository.tradeshiftGo()
			.get(`/rest/requests/${options.requestId}`)
			.then((response: AxiosResponse<GetRequestRespone>) => {
				return response.data;
			});
	},

	getRequests: (options: GetRequestsOptions = {}): Promise<any> => {
		const params: any = {};
		if (options.archived !== undefined) {
			params.archived = options.archived;
		}
		return HttpRepository.tradeshiftGo()
			.get('/rest/requests', { params })
			.then(response => {
				return response.data;
			});
	},

	reject: (options: RejectOptions): Promise<any> => {
		const data = {
			taskId: options.taskId,
		};
		return HttpRepository.tradeshiftGo()
			.post(`/rest/requests/${options.requestId}/reject`, data)
			.then(response => {
				return response.data;
			});
	},

	registerRequest: (options: RegisterRequestOptions): Promise<any> => {
		return HttpRepository.tradeshiftGo()
			.post(`/rest/requests/${options.requestId}`)
			.then(response => {
				return response.data;
			});
	},

	removeTag: (options: RemoveTagOptions): Promise<any> =>
		HttpRepository.tradeshiftGo()
			.delete(`/rest/requests/${options.requestId}/tags/${options.tag}`)
			.then(response => {
				return response.data;
			}),

	submitRequest: (options: SubmitRequestOptions): Promise<any> =>
		HttpRepository.tradeshiftGo()
			.post(`/rest/requests/${options.requestId}/submit`, options.data)
			.then(response => {
				return response.data;
			}),

	// IMPORTANT - this is NOT currently being used (supported a canceled feature)
	// PLEASE REMOVE this note if you decide to start using it
	// its purpose is to validate a particular value against a policy (a rule)
	validateRequest: (policyKey: string, policyValue: string): Promise<any> =>
		HttpRepository.tradeshiftGo()
			.get(`/rest/requests/validate/${policyKey}/${policyValue}`)
			.then(response => {
				return response.data;
			}),
};

export default RequestRepository;
